import { Subject } from 'rxjs';
import { SolitaireRoundCreator } from './solitaire-round-creator';
import { delay } from '../../common/helper';
import { Card } from '../logic/card/card';
import { PossibleMove } from '../logic/possible-moves-finder';
import { SolitaireListener } from '../game/solitaire-listener';
import { CommandListSerializer } from '../logic/commands/command-list-serializer';
import { SolitaireRemoteRoundParams } from './solitaire-remote-round-params';
import { SolitaireLocalRoundParams } from './solitaire-local-round-params';

export class SolitaireGameRunner {
  public get onNewDealStartedTrigger() {
    return this.onNewDealStarted.asObservable();
  }
  public get onNewDealFinishedTrigger() {
    return this.onNewDealFinished.asObservable();
  }
  public get onPossibleHintsTrigger() {
    return this.onPossibleHints.asObservable();
  }
  public get onBoardChangedTrigger() {
    return this.roundCreator.onBoardChangedTrigger;
  }
  public get onGameStateChangedTrigger() {
    return this.roundCreator.onGameStateChangedTrigger;
  }
  public get currentGame() {
    return this.roundCreator.currentRound?.currentGame;
  }

  private readonly solitaireListener: SolitaireListener;
  private readonly roundCreator: SolitaireRoundCreator;
  private readonly onNewDealStarted: Subject<void>;
  private readonly onNewDealFinished: Subject<void>;
  private readonly onPossibleHints: Subject<PossibleMove[]>;

  constructor(solitaireListener: SolitaireListener) {
    this.solitaireListener = solitaireListener;
    this.roundCreator = new SolitaireRoundCreator(solitaireListener);
    this.onNewDealStarted = new Subject();
    this.onNewDealFinished = new Subject();
    this.onPossibleHints = new Subject();
  }

  public startNewRemoteRound(logicVersion: number) {
    this.roundCreator.startNewRound(
      SolitaireRemoteRoundParams.create(this.solitaireListener, logicVersion),
    );

    this.onNewDealStarted.next();
    void this.newDeal();
  }

  public startExistingRemoteRound(moves: string[], logicVersion: number) {
    this.roundCreator.startNewRound(
      SolitaireRemoteRoundParams.create(this.solitaireListener, logicVersion),
    );

    this.onNewDealStarted.next();
    void this.existingDeal(moves);
  }

  public startNewLocalRound(logicVersion: number) {
    this.roundCreator.startNewRound(
      SolitaireLocalRoundParams.create(this.solitaireListener, logicVersion),
    );

    this.onNewDealStarted.next();
    void this.newDeal();
  }

  public clickDeck() {
    this.roundCreator.currentRound?.onDeckClicked();
  }

  public autoMoveCard(card: Card): boolean {
    return this.roundCreator.currentRound?.autoMoveCard(card) ?? false;
  }

  public autoFinishNextCard(): boolean {
    return this.roundCreator.currentRound?.autoFinishNextCard() ?? false;
  }

  public onCardPlaced(card: Card) {
    this.roundCreator.currentRound?.onCardPlaced(card);
  }

  public gameComplete() {
    this.roundCreator.currentRound?.onAutoFinishCompleted();
  }

  public clickStartGame() {
    this.solitaireListener.onStartGame();
  }

  public clickUndo() {
    this.roundCreator.currentRound?.undoLastCommand();
  }

  public clickHint() {
    const allMoves = this.roundCreator.currentRound?.allPossibleHints() ?? [];
    this.onPossibleHints.next(allMoves);

    if (allMoves.length === 0) {
      this.solitaireListener.showNoMoreHintsPopup();
    }
  }

  private async newDeal(): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.discloseStartingCards();

    while (game.shouldDealNextCard) {
      game.dealNextCard();
      await delay(100);
    }

    this.onNewDealFinished.next();

    this.roundCreator.currentRound?.onDealingAnimationFinished();
  }

  private async existingDeal(moves: string[]): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    game.discloseStartingCards();

    while (game.shouldDealNextCard) {
      game.dealNextCard();
    }

    const commands = moves.map((move) => CommandListSerializer.moveToCommand(move));

    for (const command of commands) {
      await game.waitForAllTasksToBeCompleted();

      if (command !== undefined) {
        game.execute(command);
      }
    }

    this.onNewDealFinished.next();

    this.roundCreator.currentRound?.onDealingAnimationFinished();
  }
}
